/**
 * Định dạng khoảng cách (mét) thành chuỗi km có 2 chữ số thập phân
 * @param distance Khoảng cách (mét)
 * @returns Chuỗi định dạng (ví dụ: "10.50 km")
 */
export const formatDistance = (distance: number | null | undefined): string => {
  if (distance == null) return '0 km';
  const km = distance / 1000;
  return `${km.toFixed(2)} km`;
};

/**
 * Định dạng thời gian (giây) thành chuỗi HH:MM:SS
 * @param seconds Thời gian (giây)
 * @returns Chuỗi định dạng (ví dụ: "01:23:45")
 */
export const formatTime = (seconds: number | null | undefined): string => {
  if (seconds == null) return '00:00:00';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  const formatNumber = (num: number) => num.toString().padStart(2, '0');
  
  return `${formatNumber(hours)}:${formatNumber(minutes)}:${formatNumber(secs)}`;
};

/**
 * Định dạng nhịp độ (giây/km) thành chuỗi MM:SS/km
 * @param pace Nhịp độ (giây/km)
 * @returns Chuỗi định dạng (ví dụ: "04:30/km")
 */
export const formatPace = (pace: number | null | undefined): string => {
  if (pace == null) return '--:--/km';
  
  // API gửi về nhịp độ dưới dạng phút/km (ví dụ: 4.0916533 có nghĩa là 4 phút 5 giây/km)
  // Chuyển đổi thành số giây/km để dễ tính toán
  const paceInSeconds = pace * 60;
  
  // Nếu nhịp độ quá nhỏ (dưới 5 giây/km), có thể là lỗi hoặc dữ liệu đặc biệt
  if (paceInSeconds < 5) {
    return '<0:05/km';
  }
  
  const minutes = Math.floor(paceInSeconds / 60);
  const seconds = Math.floor(paceInSeconds % 60);
  
  const formatNumber = (num: number) => num.toString().padStart(2, '0');
  
  return `${formatNumber(minutes)}:${formatNumber(seconds)}/km`;
};

/**
 * Định dạng vận tốc (m/s) thành chuỗi km/h có 1 chữ số thập phân
 * @param speed Vận tốc (m/s)
 * @returns Chuỗi định dạng (ví dụ: "12.5 km/h")
 */
export const formatSpeed = (speed: number | null | undefined): string => {
  if (speed == null) return '0 km/h';
  
  // Chuyển từ m/s sang km/h
  const kmPerHour = speed * 3.6;
  
  return `${kmPerHour.toFixed(1)} km/h`;
};

/**
 * Định dạng ngày từ chuỗi ISO hoặc chuỗi có định dạng YYYY-MM-DDThh:mm:ss thành YYYY-MM-DD 
 * để sử dụng trong input[type="date"]
 * @param dateString Chuỗi ngày tháng
 * @returns Chuỗi định dạng YYYY-MM-DD
 */
export const formatDateForInput = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  
  // Nếu có ký tự T (định dạng ISO), chỉ lấy phần ngày
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  
  return dateString;
};

/**
 * Định dạng ngày tháng từ mảng [năm, tháng, ngày, giờ, phút] hoặc chuỗi ISO
 * @param date Mảng ngày tháng hoặc chuỗi ISO
 * @returns Chuỗi định dạng (ví dụ: "01/01/2023")
 */
export const formatDate = (date: number[] | string | null | undefined): string => {
  if (!date) return '';
  
  let dateObj: Date;
  
  if (Array.isArray(date)) {
    // Nếu là mảng [năm, tháng, ngày, giờ, phút]
    const [year, month, day, hour = 0, minute = 0] = date;
    // Tạo chuỗi ISO trực tiếp từ mảng để tránh bị lệch ngày
    const isoString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;
    dateObj = new Date(isoString);
  } else {
    // Nếu là chuỗi ISO
    dateObj = new Date(date);
  }
  
  // Kiểm tra xem dateObj có hợp lệ không
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Định dạng ngày tháng từ mảng [năm, tháng, ngày, giờ, phút] thành chuỗi định dạng tiếng Việt
 *
 * @returns Chuỗi định dạng tiếng Việt (ví dụ: "01/01/2023, 12:30")
 */
export const formatDateFromArray = (dateArray: number[] | null): string => {
  if (!dateArray || dateArray.length < 3) {
    return 'Không xác định';
  }

  try {
    // Lấy các thành phần từ mảng
    const year = dateArray[0];
    const month = dateArray[1];
    const day = dateArray[2];
    
    // Tạo chuỗi ngày tháng nhất quán với hàm formatDate
    // Sử dụng cách tạo chuỗi trực tiếp thay vì qua đối tượng Date
    let dateString = `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
    
    // Thêm giờ phút nếu có
    if (dateArray.length >= 5) {
      const hour = dateArray[3];
      const minute = dateArray[4];
      dateString += ` ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    }
    
    return dateString;
  } catch (error) {
    console.error('Lỗi khi định dạng ngày tháng:', error);
    return 'Không xác định';
  }
};

/**
 * Định dạng độ cao (mét) thành chuỗi có 0 chữ số thập phân
 * @param elevationGain Độ cao (mét)
 * @returns Chuỗi định dạng (ví dụ: "120 m")
 */
export const formatElevation = (elevationGain: number | null | undefined): string => {
  if (elevationGain == null) return '0 m';
  return `${Math.round(elevationGain)} m`;
};

/**
 * Hàm định dạng số thành chuỗi có dấu phân cách hàng nghìn
 * @param value Số cần định dạng
 * @returns Chuỗi đã định dạng
 */
export const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '0';
  }
  return value.toLocaleString('vi-VN');
}; 